// Phase 12 — Dynamic form-fill page (offline-safe via apiClient.mutate)
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Camera, CheckCircle2, MapPin, PenLine, Star } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { mutate as offlineMutate } from "@/offline/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";

function evalConditional(field, values) {
  const c = field.conditional;
  if (!c) return true;
  const w = c.when || {};
  const actual = values[w.field_key];
  const expected = w.value;
  const op = w.op || "eq";
  if (op === "eq") return actual === expected;
  if (op === "ne") return actual !== expected;
  if (op === "gt") return Number(actual) > Number(expected);
  if (op === "lt") return Number(actual) < Number(expected);
  if (op === "in") return Array.isArray(expected) && expected.includes(actual);
  return true;
}

async function compressAndUploadPhoto(file, maxDim = 800, quality = 0.6) {
  // Compress client-side then upload via /api/photos/upload (multipart)
  const img = document.createElement("img");
  const url = URL.createObjectURL(file);
  await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = url; });
  const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(img.width * scale);
  canvas.height = Math.round(img.height * scale);
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
  URL.revokeObjectURL(url);

  const fd = new FormData();
  fd.append("file", blob, "photo.jpg");
  const { data } = await api.post("/photos/upload", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data.ref;
}

export default function FormFill() {
  const { form_id } = useParams();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const visit_id = params.get("visit_id");
  const retailer_id_qs = params.get("retailer_id");

  const [form, setForm] = useState(null);
  const [values, setValues] = useState({});
  const [gps, setGps] = useState(null);
  const [retailerId, setRetailerId] = useState(retailer_id_qs || "");
  const [retailers, setRetailers] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/activity-forms/${form_id}`);
        setForm(data);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [form_id]);

  useEffect(() => {
    // GPS auto-capture
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (p) => setGps({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
      () => {},
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  useEffect(() => {
    // Outlet picker only makes sense when the form was explicitly targeted
    // to specific retailers (Targeting → Target Retailers). An empty
    // target_retailer_ids means "not mapped to any outlet" — showing every
    // retailer in that case was misleading (looked like the form applied
    // anywhere, when really no one had configured it for any outlet).
    if (retailer_id_qs || !form) return;
    const targetIds = form.target_retailer_ids || [];
    if (targetIds.length === 0) { setRetailers([]); return; }
    (async () => {
      try {
        const { data } = await api.get("/retailers", { params: { ids: targetIds.join(","), page_size: targetIds.length } });
        setRetailers(data.items || []);
      } catch (e) { /* ignore */ }
    })();
  }, [retailer_id_qs, form]);

  const visibleFields = useMemo(() => {
    if (!form?.fields) return [];
    return form.fields.filter(f => evalConditional(f, values));
  }, [form, values]);

  function setVal(key, v) {
    setValues((prev) => ({ ...prev, [key]: v }));
  }

  async function submit() {
    if (!form) return;
    // client-side quick required-check for visible fields
    for (const f of visibleFields) {
      if (!f.required) continue;
      const v = values[f.field_key];
      const empty = v === undefined || v === null || (typeof v === "string" && !v.trim()) || (Array.isArray(v) && v.length === 0);
      if (empty) {
        toast.error(`${f.label} is required`);
        return;
      }
      if (f.field_type === "photo_pair") {
        if (!v || !v.before || !v.after) {
          toast.error(`${f.label} requires BEFORE and AFTER photos`);
          return;
        }
      }
    }
    setBusy(true);
    try {
      const res = await offlineMutate({
        method: "POST", path: "/activity-submissions", entity_type: "activity_submissions",
        body: {
          form_id, retailer_id: retailerId || null,
          visit_id: visit_id || null,
          // Only answers of fields that are visible now: a conditional field hidden after the
          // user changed an earlier answer must not keep (and submit) its old value.
          values: Object.fromEntries(visibleFields.map((f) => [f.field_key, values[f.field_key]])
            .filter(([, v]) => v !== undefined)),
          gps,
          client_ts: new Date().toISOString(),
          idempotency_key: (crypto.randomUUID?.() || `sub-${Date.now()}-${Math.random()}`).slice(0, 60),
          offline_captured: !navigator.onLine,
        },
      });
      if (res.immediate) {
        toast.success("Submitted ✓");
      } else {
        toast.success("Queued — will sync when online");
      }
      nav(params.get("return") || "/forms");      // a call form returns to the call's form list
    } catch (e) {
      const msg = e?.message || "Submit failed";
      toast.error(msg);
    } finally { setBusy(false); }
  }

  if (!form) return <div className="p-4 text-neutral-500">Loading…</div>;

  return (
    <div className="max-w-lg mx-auto p-4 space-y-4" data-testid="form-fill-page">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => nav(-1)}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="font-semibold truncate">{form.name}</h1>
          <div className="text-[11px] text-neutral-400">v{form.version} · {form.duplicate_policy?.replace(/_/g, " ")}</div>
        </div>
      </div>

      {!retailer_id_qs && (form.target_retailer_ids || []).length > 0 && (
        <div>
          <Label>Outlet (optional)</Label>
          <Select value={retailerId || "__none__"} onValueChange={(v) => setRetailerId(v === "__none__" ? "" : v)}>
            <SelectTrigger data-testid="fill-retailer"><SelectValue placeholder="Choose an outlet" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— none —</SelectItem>
              {retailers.map(r => (
                <SelectItem key={r.id} value={r.id}>{r.code} · {r.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {gps && (
        <div className="text-xs bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 text-emerald-800 flex items-center gap-2">
          <MapPin className="w-3 h-3" />
          GPS captured: {gps.lat.toFixed(5)}, {gps.lng.toFixed(5)} (±{Math.round(gps.accuracy)}m)
        </div>
      )}

      {visibleFields.map(f => (
        <FieldRenderer key={f.id} field={f} value={values[f.field_key]} onChange={(v) => setVal(f.field_key, v)} />
      ))}

      <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={submit} disabled={busy}
              data-testid="fill-submit">
        <CheckCircle2 className="w-4 h-4 mr-2" />
        {busy ? "Submitting…" : "Submit form"}
      </Button>
    </div>
  );
}


function FieldRenderer({ field, value, onChange }) {
  const t = field.field_type;
  if (t === "section_divider") {
    return <div className="border-t pt-3 text-xs uppercase tracking-widest text-neutral-500">{field.label}</div>;
  }
  if (t === "text") {
    return (
      <div>
        <Label>{field.label}{field.required && <span className="text-red-500">*</span>}</Label>
        <Input data-testid={`ff-${field.field_key}`} value={value || ""} onChange={(e) => onChange(e.target.value)} />
        {field.help_text && <p className="text-[11px] text-neutral-500 mt-1">{field.help_text}</p>}
      </div>
    );
  }
  if (t === "textarea") {
    return (
      <div>
        <Label>{field.label}{field.required && <span className="text-red-500">*</span>}</Label>
        <Textarea data-testid={`ff-${field.field_key}`} rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} />
        {field.help_text && <p className="text-[11px] text-neutral-500 mt-1">{field.help_text}</p>}
      </div>
    );
  }
  if (t === "number") {
    return (
      <div>
        <Label>{field.label}{field.required && <span className="text-red-500">*</span>}</Label>
        <Input data-testid={`ff-${field.field_key}`} type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? undefined : Number(e.target.value))} />
        {field.help_text && <p className="text-[11px] text-neutral-500 mt-1">{field.help_text}</p>}
      </div>
    );
  }
  if (t === "date") {
    return (
      <div>
        <Label>{field.label}{field.required && <span className="text-red-500">*</span>}</Label>
        <Input data-testid={`ff-${field.field_key}`} type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} />
      </div>
    );
  }
  if (t === "dropdown") {
    return (
      <div>
        <Label>{field.label}{field.required && <span className="text-red-500">*</span>}</Label>
        <Select value={value || "__none__"} onValueChange={(v) => onChange(v === "__none__" ? undefined : v)}>
          <SelectTrigger data-testid={`ff-${field.field_key}`}><SelectValue placeholder="Choose…" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">— none —</SelectItem>
            {(field.options || []).map(o => <SelectItem key={o.code} value={o.code}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    );
  }
  if (t === "multiselect") {
    const arr = Array.isArray(value) ? value : [];
    return (
      <div>
        <Label>{field.label}{field.required && <span className="text-red-500">*</span>}</Label>
        <div className="grid grid-cols-2 gap-2 mt-1" data-testid={`ff-${field.field_key}`}>
          {(field.options || []).map(o => (
            <label key={o.code} className="flex items-center gap-2 text-sm">
              <Checkbox checked={arr.includes(o.code)}
                        onCheckedChange={(chk) => onChange(chk ? [...arr, o.code] : arr.filter(x => x !== o.code))} />
              {o.label}
            </label>
          ))}
        </div>
      </div>
    );
  }
  if (t === "checkbox") {
    return (
      <div className="flex items-center justify-between border rounded-md p-3">
        <Label>{field.label}</Label>
        <Switch data-testid={`ff-${field.field_key}`} checked={!!value} onCheckedChange={onChange} />
      </div>
    );
  }
  if (t === "rating") {
    const n = Number(value) || 0;
    return (
      <div>
        <Label>{field.label}{field.required && <span className="text-red-500">*</span>}</Label>
        <div className="flex gap-1 mt-1" data-testid={`ff-${field.field_key}`}>
          {[1,2,3,4,5].map(i => (
            <button key={i} type="button" onClick={() => onChange(i)}
                    className="p-1">
              <Star className={`w-6 h-6 ${i <= n ? "fill-amber-400 text-amber-400" : "text-neutral-300"}`} />
            </button>
          ))}
        </div>
      </div>
    );
  }
  if (t === "photo") {
    return <PhotoUploader label={field.label + (field.required ? " *" : "")} testId={`ff-${field.field_key}`}
                          value={value} onChange={onChange} />;
  }
  if (t === "photo_pair") {
    const v = value || {};
    return (
      <div>
        <Label>{field.label}{field.required && <span className="text-red-500">*</span>}</Label>
        <div className="grid grid-cols-2 gap-2 mt-1">
          <PhotoUploader label="Before" testId={`ff-${field.field_key}-before`}
            value={v.before} onChange={(ref) => onChange({ ...v, before: ref })} />
          <PhotoUploader label="After" testId={`ff-${field.field_key}-after`}
            value={v.after} onChange={(ref) => onChange({ ...v, after: ref })} />
        </div>
      </div>
    );
  }
  if (t === "signature") {
    return <SignatureCapture label={field.label + (field.required ? " *" : "")}
                              testId={`ff-${field.field_key}`}
                              value={value} onChange={onChange} />;
  }
  if (t === "gps") {
    return (
      <div className="border rounded-md p-3">
        <Label>{field.label}</Label>
        <div className="text-xs text-neutral-500 mt-1">Auto-captured (see top of screen)</div>
      </div>
    );
  }
  return <div className="text-xs text-neutral-400">Unsupported field type: {t}</div>;
}


function PhotoUploader({ label, testId, value, onChange }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  async function onFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const ref = await compressAndUploadPhoto(file);
      onChange(ref);
      toast.success("Photo uploaded");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); e.target.value = ""; }
  }
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <input ref={inputRef} type="file" accept="image/*" capture="environment" className="hidden"
             onChange={onFile} data-testid={`${testId}-input`} />
      <button type="button" onClick={() => inputRef.current?.click()}
              data-testid={testId}
              disabled={busy}
              className={`w-full border rounded-md p-3 text-center transition ${
                value ? "bg-emerald-50 border-emerald-500" : "bg-white border-dashed border-neutral-300 hover:border-emerald-400"
              }`}>
        {busy ? "Uploading…" : value ? (
          <span className="text-emerald-700 text-xs flex items-center justify-center gap-1">
            <CheckCircle2 className="w-3 h-3" /> Captured
          </span>
        ) : (
          <span className="text-neutral-500 text-sm flex items-center justify-center gap-1">
            <Camera className="w-4 h-4" /> Tap to capture
          </span>
        )}
      </button>
    </div>
  );
}


function SignatureCapture({ label, testId, value, onChange }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [busy, setBusy] = useState(false);

  function point(e) {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches?.[0]?.clientX ?? e.clientX) - rect.left;
    const y = (e.touches?.[0]?.clientY ?? e.clientY) - rect.top;
    return { x, y };
  }
  function start(e) {
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = point(e);
    ctx.beginPath(); ctx.moveTo(x, y);
    setDrawing(true);
  }
  function move(e) {
    if (!drawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = point(e);
    ctx.lineTo(x, y); ctx.strokeStyle = "#111"; ctx.lineWidth = 2; ctx.lineCap = "round"; ctx.stroke();
  }
  function end() { setDrawing(false); }
  function clear() {
    const canvas = canvasRef.current;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    onChange(null);
  }
  async function save() {
    const canvas = canvasRef.current;
    const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
    if (!blob) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, "sig.png");
      const { data } = await api.post("/photos/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.ref);
      toast.success("Signature saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  return (
    <div>
      <Label>{label}</Label>
      <canvas ref={canvasRef} width={400} height={140}
              className="w-full border rounded-md bg-white mt-1 touch-none"
              onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
              onTouchStart={start} onTouchMove={move} onTouchEnd={end}
              data-testid={testId} />
      <div className="flex gap-2 mt-1">
        <Button size="sm" variant="outline" onClick={clear}>Clear</Button>
        <Button size="sm" onClick={save} disabled={busy}>
          <PenLine className="w-3 h-3 mr-1" /> {busy ? "Saving…" : (value ? "Re-save" : "Save signature")}
        </Button>
        {value && <span className="text-xs text-emerald-700 self-center flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Saved</span>}
      </div>
    </div>
  );
}
